import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Space,
  Tag,
  Spin,
  Image,
  Alert
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  FlagOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import MapPointPreview from '@iso/components/MapPointPreview';
import Box from '@iso/components/utility/box';
import HazardPageWrapper from './Hazard.styles';
import { hazardService } from '@iso/services/hazard.service';
import {
  getStatusLabel,
  getStatusColor
} from '@iso/constants/hazardStatus';
import { getSafetyActivityHazardLabel, getSafetyActivityHazardCategoryLabel, isBiosecurityHazardCategory } from '@iso/constants/hazardTypes';
import {
  getBiosecurityReportTypeLabel,
  getBiosecurityEventTypeLabel,
  getBiosecurityEventStatusLabel,
  getBiosecurityDomainLabel,
} from '@iso/constants/biosecurityTaxonomy';
import HazardAssignmentCard from '@iso/components/Hazards/HazardAssignmentCard';
import HazardTimeline from '@iso/components/Hazards/HazardTimeline';
import ResolveHazardModal from '@iso/components/Hazards/ResolveHazardModal';
import ReopenHazardModal from '@iso/components/Hazards/ReopenHazardModal';
import ManualEscalateModal from '@iso/components/Hazards/ManualEscalateModal';
import ConfirmImmediateResolutionModal from '@iso/components/Hazards/ConfirmImmediateResolutionModal';
import ModalActivity from '@iso/containers/Pages/HealthSafety/ModalActivity';
import { commonHelper } from '@iso/lib/helpers/commonHelper';

/**
 * A "GPS: -27.560123, 151.951022" line inside a hazard description.
 *
 * Anchored to the start of a line so a coordinate mentioned inside prose is not
 * pulled out of its sentence.
 */
const GPS_LINE = /^GPS:\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;

/**
 * Render a hazard description, drawing any GPS line as a map.
 *
 * The description is a plain text blob built by BiosecurityHazardService, and
 * for everything except the coordinate that is the right shape. But a pair of
 * decimals does not answer the only question a reader has about it — where is
 * that? — and the person reading a hazard is usually deciding whether to drive
 * there.
 *
 * The line is matched rather than the hazard's own `point` used, deliberately.
 * `point` is always set (it falls back to the property's centre when nobody
 * shared a position), so mapping it would draw a confident pin on a place
 * nobody reported. The GPS line exists only when a coordinate was actually
 * shared, which makes it the honest thing to map.
 */
const renderDescription = (description) => {
  if (!description) {
    return null;
  }

  const lines = String(description).split('\n');

  return lines.map((line, index) => {
    const match = line.match(GPS_LINE);

    if (!match) {
      return <div key={index}>{line}</div>;
    }

    return (
      <div key={index} style={{ margin: '8px 0' }}>
        <MapPointPreview lat={match[1]} lng={match[2]} label="GPS" />
      </div>
    );
  });
};

const HazardDetailView = () => {
  const { id } = useParams();
  const history = useHistory();
  const currentUserId = useSelector((state) => state.user?.user?.id);

  const [hazard, setHazard] = useState(null);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [reopenModalVisible, setReopenModalVisible] = useState(false);
  const [escalateModalVisible, setEscalateModalVisible] = useState(false);
  const [confirmResolutionModalVisible, setConfirmResolutionModalVisible] = useState(false);

  useEffect(() => {
    fetchHazardDetails();
  }, [id]);

  const handleCloseEditModal = useCallback(() => setEditModalVisible(false), []);

  const fetchHazardDetails = async () => {
    setLoading(true);
    try {
      const result = await hazardService.viewHazard(id);
      if (result && result.hazard) {
        setHazard(result.hazard);
      }
    } catch (error) {
      console.error('Failed to fetch hazard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !hazard) {
    return (
      <LayoutWrapper>
        <Box>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
          </div>
        </Box>
      </LayoutWrapper>
    );
  }

  if (!hazard) {
    return (
      <LayoutWrapper>
        <Box>
          <div style={{ padding: '24px' }}>
            <Alert message="Hazard not found" type="error" />
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push('/hazards')}
              style={{ marginTop: 16 }}
              className="hazard-back-button"
            >
              Back to List
            </Button>
          </div>
        </Box>
      </LayoutWrapper>
    );
  }

  // Permission checks
  const canResolve = hazard.status !== 'resolved' && hazard.status !== 'closed';
  const canEscalate = hazard.current_escalation_level < 3 && hazard.is_closed !== true && hazard.status !== 'resolved' && hazard.status !== 'closed';
  const canReopen = hazard.status === 'resolved' || hazard.status === 'closed';
  // Mirrors HazardList.canEditHazard: requires a logged-in user, blocks resolved/closed,
  // otherwise permissive — backend enforces actual edit permission.
  const canEdit = !!currentUserId && hazard.status !== 'resolved' && hazard.status !== 'closed';
  // const needsConfirmation = hazard.status === 'to_be_confirmed' || hazard.status === 'closed';
  const needsConfirmation = false;

  // Get all photos
  const photos = hazard.attachments || [];

  return (
    <LayoutWrapper>
      <HazardPageWrapper>
        <Box id="containerBox">
          <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => history.push('/hazards')}
                style={{ marginBottom: 16, height: 40, borderRadius: 8 }}
                className="hazard-back-button"
              >
                Back to List
              </Button>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h1 style={{ margin: 0, marginBottom: 8 }}>
                    {getSafetyActivityHazardLabel(hazard.hazard_type) || hazard.title}
                  </h1>
                  <Space>
                    <Tag color={getStatusColor(hazard.status)}>
                      {getStatusLabel(hazard.status)}
                    </Tag>
                  </Space>
                </div>

                <Space wrap>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setEditModalVisible(true)}
                    disabled={!canEdit}
                    style={{ height: 40, borderRadius: 8 }}
                  >
                    Edit Hazard
                  </Button>
                  {needsConfirmation && (
                    <Button
                      type="primary"
                      className="btn-success"
                      icon={<FlagOutlined />}
                      onClick={() => setConfirmResolutionModalVisible(true)}
                    >
                      Confirm Resolution
                    </Button>
                  )}
                  {canResolve && (
                    <Button
                      type="primary"
                      className="btn-success"
                      icon={<CheckCircleOutlined />}
                      onClick={() => setResolveModalVisible(true)}
                    >
                      Rectified
                    </Button>
                  )}
                  {canEscalate && (
                    <Button
                      danger
                      icon={<ArrowUpOutlined />}
                      onClick={() => setEscalateModalVisible(true)}
                      style={{ height: 40, borderRadius: 8 }}
                    >
                      Escalate
                    </Button>
                  )}
                  {canReopen && (
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => setReopenModalVisible(true)}
                      style={{ height: 40, borderRadius: 8 }}
                    >
                      Reopen
                    </Button>
                  )}
                </Space>
              </div>
            </div>

            {/* Alert for pending actions */}
            {needsConfirmation && (
              <Alert
                message="Manager Action Required"
                description="This hazard has immediate resolution pending your confirmation."
                type="warning"
                showIcon
                closable
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Hazard Information */}
            <Card title="Hazard Information" style={{ marginBottom: 16 }}>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(hazard.status)}>
                    {getStatusLabel(hazard.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Escalation Level">
                  <Tag color={hazard.current_escalation_level === 3 ? 'red' : hazard.current_escalation_level === 2 ? 'orange' : 'blue'}>
                    Level {hazard.current_escalation_level}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  {getSafetyActivityHazardCategoryLabel(hazard.hazard_category) || hazard.hazard_category || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  {getSafetyActivityHazardLabel(hazard.hazard_type) || hazard.hazard_type}
                </Descriptions.Item>
                {/* Biosecurity classification. Rendered only for biosecurity
                    hazards; every other category leaves these null, and showing
                    four empty rows on every hazard would be noise. */}
                {isBiosecurityHazardCategory(hazard.hazard_category) && (
                  <Descriptions.Item label="Report Type">
                    {getBiosecurityReportTypeLabel(hazard.report_type) || 'N/A'}
                  </Descriptions.Item>
                )}
                {isBiosecurityHazardCategory(hazard.hazard_category) && (
                  <Descriptions.Item label="Event Type">
                    {getBiosecurityEventTypeLabel(hazard.event_type) || 'N/A'}
                  </Descriptions.Item>
                )}
                {isBiosecurityHazardCategory(hazard.hazard_category) && (
                  <Descriptions.Item label="Event Status">
                    {getBiosecurityEventStatusLabel(hazard.event_status) || 'N/A'}
                  </Descriptions.Item>
                )}
                {isBiosecurityHazardCategory(hazard.hazard_category) && (
                  <Descriptions.Item label="Domain">
                    {getBiosecurityDomainLabel(hazard.domain) || 'N/A'}
                  </Descriptions.Item>
                )}
                {isBiosecurityHazardCategory(hazard.hazard_category) && hazard.reporter_name && (
                  <Descriptions.Item label="Reported By">
                    {hazard.reporter_name}
                    {hazard.reporter_phone ? ` (${hazard.reporter_phone})` : ''}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Created By">
                  <Space>
                    <UserOutlined />
                    {hazard.creator?.name || 'Unknown'}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {commonHelper.displayDateTimeLocal(hazard.created_at)}
                </Descriptions.Item>
                <Descriptions.Item label="Last Updated">
                  {commonHelper.displayDateTimeLocal(hazard.updated_at)}
                </Descriptions.Item>
                {hazard.escalated_at && (
                  <Descriptions.Item label="Last Escalated">
                    {commonHelper.displayDateTimeLocal(hazard.escalated_at)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Description" span={2}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {renderDescription(hazard.description)}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Current Assignment */}
            <HazardAssignmentCard
              hazard={hazard}
              workflowState={hazard.workflow_state}
              currentAssignment={hazard.current_assignment}
            />

            {/* Rectification Notes (if rectified, closed, or pending confirmation) */}
            {(hazard.status === 'resolved' || hazard.status === 'closed' || hazard.status === 'to_be_confirmed') && hazard.workflow_state?.resolution_notes && (
              <Card
                title="Rectification Information"
                style={{ marginBottom: 16 }}
              >
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="How was the hazard rectified?">
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {hazard.workflow_state.resolution_notes}
                    </div>
                  </Descriptions.Item>
                  {hazard.workflow_state.resolved_by_user && (
                    <Descriptions.Item label="Rectified By">
                      <Space>
                        <UserOutlined />
                        {hazard.workflow_state.resolved_by_user.name}
                      </Space>
                    </Descriptions.Item>
                  )}
                  {hazard.workflow_state.resolved_at && (
                    <Descriptions.Item label="Rectified At">
                      {commonHelper.displayDateTimeLocal(hazard.workflow_state.resolved_at)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Photos */}
            {photos.length > 0 && (
              <Card title={`Photos (${photos.length})`} style={{ marginBottom: 16 }}>
                <Image.PreviewGroup>
                  <Space wrap>
                    {photos.map((photo, index) => (
                      <Image
                        key={photo.id || index}
                        width={150}
                        height={150}
                        src={photo.full_path || photo.url || photo.path}
                        alt={`Photo ${index + 1}`}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </Card>
            )}

            {/* Timeline */}
            {/* <HazardTimeline hazardId={hazard.id} /> */}
          </div>

          {/* Modals */}
          <ModalActivity
            visibleModalActivity={editModalVisible}
            cancelModalActivity={handleCloseEditModal}
            activityActive="hazard"
            editingPolicy={hazard}
            propertyId={hazard.property_id}
            searchAssetAndPolicy={fetchHazardDetails}
            saveToHazardsTable={true}
          />

          <ResolveHazardModal
            visible={resolveModalVisible}
            hazardId={hazard.id}
            onCancel={() => setResolveModalVisible(false)}
            onSuccess={() => {
              setResolveModalVisible(false);
              fetchHazardDetails();
            }}
          />

          <ReopenHazardModal
            visible={reopenModalVisible}
            hazardId={hazard.id}
            hazardTitle={hazard.title}
            onCancel={() => setReopenModalVisible(false)}
            onSuccess={() => {
              setReopenModalVisible(false);
              fetchHazardDetails();
            }}
          />

          <ManualEscalateModal
            visible={escalateModalVisible}
            hazardId={hazard.id}
            hazardTitle={getSafetyActivityHazardLabel(hazard.hazard_type) || hazard.title}
            currentLevel={hazard.current_escalation_level}
            onCancel={() => setEscalateModalVisible(false)}
            onSuccess={() => {
              setEscalateModalVisible(false);
              fetchHazardDetails();
            }}
          />

          <ConfirmImmediateResolutionModal
            visible={confirmResolutionModalVisible}
            hazard={hazard}
            workflowState={hazard.workflow_state}
            onCancel={() => setConfirmResolutionModalVisible(false)}
            onSuccess={() => {
              setConfirmResolutionModalVisible(false);
              fetchHazardDetails();
            }}
          />
        </Box>
      </HazardPageWrapper>
    </LayoutWrapper>
  );
};

export default HazardDetailView;
